import { TooltipProps } from '@column/column-ui-kit';
import { cn } from '@column/ui';

import { TransferEntryType } from '~/repositories';
import { formatString } from '~/util';

import { TransferProgress, TransferStatus } from './types';
import { BadgeTone } from './utils';

export const formatTransferStatus = (status: TransferStatus): string => {
  if (status.toLowerCase() === 'pre_review') {
    return 'Pending approval';
  }
  return formatString(status);
};

export const getTransferStatusTooltip = (status: TransferStatus): TooltipProps | undefined => {
  if (transferStatusTooltips.has(status)) {
    return {
      content: transferStatusTooltips.get(status),
      delay: 200,
      triggerClick: false,
    };
  }
  return undefined;
};

export const getStatusBadgeTone = (progress: TransferProgress, status: TransferStatus): BadgeTone => {
  if (progress === TransferProgress.PROGRESS_FAILED) return 'danger';
  if (progress === TransferProgress.PROGRESS_100) return 'success';

  const dangerStatuses = [
    TransferStatus.BLOCKED,
    TransferStatus.DENIED,
    TransferStatus.REJECTED,
    TransferStatus.RETURNED,
    TransferStatus.STOPPED,
  ];
  if (dangerStatuses.includes(status)) return 'danger';

  const successStatuses = [TransferStatus.APPROVED, TransferStatus.DEPOSITED, TransferStatus.COMPLETED];
  if (successStatuses.includes(status)) return 'success';

  if (status === TransferStatus.HOLD) return 'info';

  return 'secondary';
};

export const getStatusIconClassName = (tone: BadgeTone) =>
  cn(
    'inline-flex shrink-0 items-center justify-center [&>svg]:[--icon-size-x:16px] [&>svg]:[--icon-size-y:16px] [&>svg]:[--icon-size:16px]',
    tone === 'secondary' && 'text-muted-foreground',
    tone === 'success' && 'text-seafoam-600 dark:text-seafoam-600',
    tone === 'danger' && 'text-red-500 dark:text-red-500',
    tone === 'info' && 'text-blue-500 dark:text-blue-400'
  );

export const formatTransferStatusLabel = (status: TransferStatus, transferType: TransferEntryType): string => {
  if (transferType === 'wire-drawdown') {
    switch (status.toLowerCase()) {
      case 'approved':
        return 'Request Approved';
      case 'sent':
        return 'Request Sent';
      case 'initiated':
        return 'Request Initiated';
      case 'received':
        return 'Request Received';
      case 'rejected':
        return 'Request Rejected';
      case 'denied':
        return 'Request Denied';
      default:
        return formatTransferStatus(status);
    }
  }

  return formatTransferStatus(status);
};

const transferStatusTooltips = new Map<TransferStatus, string>([
  [TransferStatus.APPROVED, 'The wire drawdown request has been approved and is ready for processing.'],
  [TransferStatus.CANCELED, 'The transaction was canceled, and no funds were moved.'],
  [
    TransferStatus.COMPLETED,
    'The transaction has been fully processed and the funds have successfully reached the intended destination.',
  ],
  [TransferStatus.DENIED, 'The wire drawdown request was denied and will not be processed.'],
  [TransferStatus.DEPOSITED, 'Funds have been deposited into the account as scheduled.'],
  [TransferStatus.FAILED, 'The transaction could not be processed due to an error or rejection.'],
  [
    TransferStatus.FIRST_RETURN,
    'The first attempt to process the transaction was returned, possibly due to issues like NSF (Non-Sufficient Funds).',
  ],
  [TransferStatus.HOLD, 'Funds or the transaction are currently on hold, pending further verification or action.'],
  [TransferStatus.INITIATED, 'The transaction has been initiated and is pending completion of processing steps.'],
  [TransferStatus.ISSUED, 'A check or other payment form has been issued but not yet cashed or deposited.'],
  [
    TransferStatus.MANUAL_REVIEW,
    'The transaction is undergoing manual review to verify its details or source before proceeding.',
  ],
  [TransferStatus.MANUAL_REVIEW_APPROVED, 'The transaction has passed manual review and is approved for processing.'],
  [TransferStatus.PENDING_DEPOSIT, 'The deposit has been initiated but is not yet complete.'],
  [
    TransferStatus.PENDING_FIRST_RETURN,
    'A return has been initiated on the first attempt of processing the transaction, awaiting finalization.',
  ],
  [TransferStatus.PENDING_RECLEAR, 'The transaction is undergoing an attempt to clear after an initial failure.'],
  [TransferStatus.PENDING_RETURN, 'A return on the transaction has been initiated and is currently being processed.'],
  [
    TransferStatus.PENDING_REVIEW,
    'The incoming international wire is on hold while creditor or debtor data is reviewed and repaired.',
  ],
  [TransferStatus.PENDING_SECOND_RETURN, 'The second attempt to return the transaction is underway.'],
  [TransferStatus.PENDING_STOP, 'A stop payment request has been submitted and is pending action.'],
  [TransferStatus.PENDING_SUBMISSION, 'The transaction is prepared and pending submission to the clearing system.'],
  [TransferStatus.PENDING_USER_INITIATED_RETURN, 'A return initiated by the user is currently being processed.'],
  [TransferStatus.PRE_REVIEW, 'The transaction is pending approval and has not yet been initiated.'],
  [TransferStatus.RECEIVED, 'The wire drawdown request has been received and is being processed.'],
  [TransferStatus.RECLEARED, 'The transaction has been cleared again following a previous clearance issue.'],
  [
    TransferStatus.REJECTED,
    'The transaction was rejected by the receiving party or due to an issue with the transaction details.',
  ],
  [TransferStatus.RETURN_CONTESTED, 'The dishonored return of the transaction has been rejected by the receiving bank'],
  [TransferStatus.RETURN_DISHONORED, 'the return was not accepted or honored by the originating bank.'],
  [TransferStatus.RETURNED, 'The transaction was returned by the receiving party or due to a failure in processing.'],
  [TransferStatus.SCHEDULED, 'The transaction is scheduled for a future date and has not yet been processed.'],
  [TransferStatus.SECOND_RETURN, 'The transaction has been returned for a second time, indicating persistent issues.'],
  [TransferStatus.SENT, 'The wire drawdown request has been sent to the receiving party.'],
  [TransferStatus.SETTLED, 'Funds movement is complete.'],
  [TransferStatus.STOPPED, 'The transaction has been stopped, typically upon the request of the sender.'],
  [
    TransferStatus.SUBMITTED,
    'The transaction has been submitted to the processing network and is currently being processed.',
  ],
  [TransferStatus.USER_INITIATED_RETURNED, 'A return initiated by the user has been processed and completed.'],
  [
    TransferStatus.USER_INITIATED_RETURN_SUBMITTED,
    'A return initiated by the user has been submitted and is pending processing.',
  ],
  [
    TransferStatus.USER_INITIATED_RETURN_DISHONORED,
    'A user-initiated return was not honored, typically due to procedural errors or disputes.',
  ],
]);
